import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ADD_OBSERVATION_COLUMNS_DATA,
  INVESTIGATION_OBSERVATION_COLUMNS_DATA,
  observationValuesEquivalent,
  isTableObservationInput,
} from "./config";
import { useAlert } from "context/alert";
import { useDispatch, useSelector } from "react-redux";
import TitleComponent from "./TitleComponet";
import InvestigationResults from "./InvestigationResults";
import Table from "components/Common/Table/index";
import {
  createPatientLabResultEntry,
  fetchInvestigationObservationDropDown,
  fetchObservationResultEntryData,
  fetchOnlyOrganismData,
  fetchReportRoleById,
} from "services/resultEntryReportMaster";
import InternalDoctorCheckbox from "components/Common/Checkbox/InternalDoctorCheckbox";
import Loader from "components/Common/loader";
import { STALE_TIME } from "utils/constants";
import CommentNameDropdown from "components/Common/Dropdowns/CommentNameDropdown";
import TextEditor from "components/Common/TextEditor/editor";
import Swal from "sweetalert2";
import {
  resetResultEntryObservations,
  setResultEntryObservations,
  updateResultEntryObservation,
} from "../../../redux/action/result_entry_observation_action";
import TemplateDropDown from "components/Common/Dropdowns/TemplateDropDown";
import { fetchInvestigationObservations } from "services/investigationManager";
import zoneLocalStorageId from "utils/zone";
import { getInvestigationComment, getResultEntrySaveError } from "utils/resultEntryComments";
import { useNavigate } from "react-router-dom";

const getInvestigationRowKey = (row) => {
  const appointmentId = row?.appointment_id;
  const investigationId =
    row?.investigation_data?.investigation_id || row?.investigation_id;

  if (!appointmentId || !investigationId) {
    return null;
  }

  return `result-entry-row-${appointmentId}-${investigationId}`;
};

// function constructFormulasForFilledValues(filledValues, observations) {
//   const result = observations.filter((observation) => {
//     const array = [];
//     observation.fields.forEach((field) => {
//       const observation = filledValues.find(
//         (item) => item.id === parseInt(field.replace("#", ""))
//       );
//       if (observation) array.push(observation.value);
//     });
//     if (array.length === observation.fields.length) return true;
//     return false;
//   });
//   return result.map((observation) => {
//     let formula = observation.formula;

//     console.log("observation.formula", formula);

//     let observationValue = null;
//     const filledValue = filledValues.find(
//       (v) => v.id === observation.id && v?.isDirectChange
//     );
//     if (filledValue) observationValue = filledValue.value;
//     observation.fields.forEach((field) => {
//       const id = parseInt(field.replace("#", "").replace("@", ""));
//       const found = filledValues.find((item) => item.id === id);
//       if (found) {
//         formula = formula.replace(new RegExp(`#${id}@`, "g"), found.value);
//       }
//     });

//     if (!observationValue) {
//       try {
//         observationValue = eval(formula);
//       } catch (error) {
//         console.error(
//           `Error evaluating formula: ${observation.formula}`,
//           error
//         );
//       }
//     }

//     return { ...observation, observationValue };
//   });
// }

function getObservationRowFromGrid(obsId, observationDataRedux) {
  if (!observationDataRedux || typeof observationDataRedux !== "object") {
    return null;
  }

  for (const department of Object.keys(observationDataRedux)) {
    const investigations = observationDataRedux[department];
    if (!investigations || typeof investigations !== "object") continue;

    for (const investigation of Object.keys(investigations)) {
      const observations =
        investigations[investigation]?.observations || [];
      const match = observations.find(
        (item) => +item.observation_id === +obsId
      );
      if (match) return match;
    }
  }

  return null;
}

function getObservationValueFromGrid(obsId, observationDataRedux) {
  const match = getObservationRowFromGrid(obsId, observationDataRedux);
  if (!match) return null;

  const raw =
    match.observation_value !== undefined &&
    match.observation_value !== null &&
    match.observation_value !== ""
      ? match.observation_value
      : match.default_reading;

  if (raw === undefined || raw === null || raw === "") {
    return null;
  }

  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
}

function getSelfReferenceFormulaInput(obsId, filledValues, observationDataRedux) {
  const row = getObservationRowFromGrid(obsId, observationDataRedux);
  if (row?.machine_reading !== undefined && row?.machine_reading !== null && row?.machine_reading !== "") {
    const fromMachine = Number(row.machine_reading);
    if (Number.isFinite(fromMachine)) return fromMachine;
  }

  return getNumericObsValue(obsId, filledValues, observationDataRedux) ?? 0;
}

function getNumericObsValue(obsId, filledValues, observationDataRedux) {
  const filled = filledValues.find((item) => +item.id === +obsId);
  if (filled && filled.value !== undefined && filled.value !== null) {
    const fromFilled = Number(filled.value);
    if (Number.isFinite(fromFilled)) return fromFilled;
  }

  return getObservationValueFromGrid(obsId, observationDataRedux);
}

function hasResolvableObsValue(obsId, filledValues, observationDataRedux) {
  const value = getNumericObsValue(obsId, filledValues, observationDataRedux);
  return value !== null && !Number.isNaN(value);
}

function isEgfrObservationName(name) {
  return /\begfr\b/i.test(name || "");
}

function constructFormulasForFilledValues(
  filledValues,
  observations,
  patientAgeForFormula,
  observationDataRedux,
  patientGender
) {
  const result = observations.filter((observation) => {
    if (+observation.id === 44) return true;

    return observation.fields.every((field) => {
      const obsId = parseInt(
        String(field).replace(/#/g, "").replace("@", ""),
        10
      );
      return hasResolvableObsValue(obsId, filledValues, observationDataRedux);
    });
  });

  const dbResults = result.map((observation) => {
    let formula = observation.formula;

    let observationValue = null;
    const filledValue =
      +observation.id === 44
        ? null
        : filledValues.find(
            (v) => +v.id === +observation.id && v?.isDirectChange
          );
    if (filledValue) observationValue = filledValue.value;

    observation.fields.forEach((field) => {
      const id = parseInt(
        String(field).replace(/#/g, "").replace("@", ""),
        10
      );
      const valueForField =
        +id === +observation.id
          ? getSelfReferenceFormulaInput(
              id,
              filledValues,
              observationDataRedux
            )
          : getNumericObsValue(id, filledValues, observationDataRedux) ?? 0;
      formula = formula.replace(
        new RegExp(`#?${id}@`, "g"),
        String(valueForField)
      );
    });

    if (observationValue === null || observationValue === undefined) {
      try {
        if (
          typeof patientAgeForFormula === "number" &&
          !Number.isNaN(patientAgeForFormula)
        ) {
          formula = formula.replace(/_AGE\b/g, String(patientAgeForFormula));
        } else if (/_AGE\b/.test(formula)) {
          throw new Error("Age not available for formula containing _AGE");
        }

        if (formula.includes("Math.pow(") && formula.includes("*")) {
          const mathPowRegex = /Math\.pow\(([^)]+)\)/g;
          formula = formula.replace(mathPowRegex, (match, args) => {
            if (args.includes("*")) {
              const parts = args.split(",");
              if (parts.length === 2) {
                const base = eval(parts[0].trim());
                const exponent = eval(parts[1].trim());
                return Math.pow(base, exponent);
              }
              const evaluatedArgs = eval(args);
              return Math.pow(evaluatedArgs, 2);
            }
            return match;
          });
        }

        observationValue = eval(formula);
      } catch (error) {
        console.error(
          `Error evaluating formula: ${observation.formula}`,
          error
        );
      }
    }

    return { ...observation, observationValue };
  });

  const merged = new Map(dbResults.map((f) => [+f.id, f]));
  if (
    typeof patientAgeForFormula === "number" &&
    !Number.isNaN(patientAgeForFormula) &&
    observationDataRedux
  ) {
    const gender = String(patientGender || "").toLowerCase();
    const isFemale = gender === "f" || gender === "female";
    const kappa = isFemale ? 0.7 : 0.9;
    const alpha = isFemale ? -0.241 : -0.302;
    const femaleMult = isFemale ? 1.012 : 1;

    for (const department of Object.keys(observationDataRedux)) {
      const investigations = observationDataRedux[department];
      if (!investigations || typeof investigations !== "object") continue;

      for (const investigation of Object.keys(investigations)) {
        const gridObs =
          investigations[investigation]?.observations || [];
        const creatinineObs =
          gridObs.find((o) => /serum\s*creatinine/i.test(o.observation_name || "")) ||
          gridObs.find((o) => /creatinine/i.test(o.observation_name || ""));
        if (!creatinineObs) continue;

        const scr = getNumericObsValue(
          creatinineObs.observation_id,
          filledValues,
          observationDataRedux
        );
        if (scr == null || scr <= 0) continue;

        const scrKappa = scr / kappa;
        const egfrValue =
          142 *
          Math.min(scrKappa, 1) ** alpha *
          Math.max(scrKappa, 1) ** -1.2 *
          0.9938 ** patientAgeForFormula *
          femaleMult;

        for (const obs of gridObs) {
          if (!isEgfrObservationName(obs.observation_name)) continue;
          const obsId = obs.observation_id;
          if (filledValues.find((v) => +v.id === +obsId && v?.isDirectChange)) {
            continue;
          }
          merged.set(+obsId, {
            id: obsId,
            department,
            investigation,
            observationValue: Math.round(egfrValue),
          });
        }
      }
    }
  }

  return Array.from(merged.values());
}

function constructFormulaFieldsFromObservations(observation) {
  const departmentKeys = Object.keys(
    observation.department_investigation || {}
  );

  return departmentKeys.flatMap((department) => {
    const investigationKeys = Object.keys(
      observation.department_investigation[department] || {}
    );

    return investigationKeys.flatMap((investigation) => {
      return observation.department_investigation[department][
        investigation
      ]?.observations
        .map((item) => ({
          id: item.observation_id,
          department,
          investigation,
          name: item.observation_name.trim(),
          formula: item.observation_formula,
          fields:
            item.observation_formula
              ?.match(/#?\d+@/g)
              ?.map((field) => field.trim()) || [],
        }))
        .filter((item) => item.formula);
    });
  });
}

function endsWithDotAndZeros(str) {
  const dotIndex = str.lastIndexOf(".");
  if (dotIndex === -1) return false;

  const afterDot = str.slice(dotIndex + 1);
  return afterDot.length > 0 && /^0+$/.test(afterDot);
}

function hasManualObservationOverride(filledObservations, id) {
  return filledObservations.some(
    (entry) => +entry.id === +id && entry.isDirectChange
  );
}

function formulasArrayEqual(prev, next) {
  if (prev.length !== next.length) {
    return false;
  }
  return prev.every(
    (item, index) =>
      +item.id === +next[index].id &&
      String(item.observationValue) === String(next[index].observationValue)
  );
}

const AddObservationValue = ({
  onClose,
  rowData,
  handleDocuments,
  documentInfo,
  refetchBussinessReport,
  isOpenObservationValue,
}) => {
  const dispatch = useDispatch();
  const observationDataRedux = useSelector(
    (state) => state.resultEntryObservationReducer
  );

  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [updatedRows, setUpdatedRows] = useState({});
  const jwtAuthreducer = useSelector((state) => state.jwtAuthreducer);
  const [filterValue, setFilterValue] = useState();
  const [doctorId, setdoctorId] = useState();
  // const [comment, setComment] = useState("");
  const [investigationId, setInvestigationId] = useState(
    rowData?.investigation_data?.investigation_id
  );
  // const [commentName, setCommentName] = useState();
  const [commentsMap, setCommentsMap] = useState({});
  const [commentNamesMap, setCommentNamesMap] = useState({});
  const [templateDescription, setTemplateDescription] = useState();
  const [status, setStatus] = useState(null);
  const [selectedInvestigationIds, setSelectedInvestigationIds] = useState([]);
  const [selectedInvestigationId, setSelectedInvestigationId] = useState();
  const [selectedInvestigation, setSelectedInvestigation] = useState(null);
  const [selectedInvestigations, setSelectedInvestigations] = useState([]);
  const [investigationDataMap, setInvestigationDataMap] = useState({});
  const [constructedObservations, setConstructedObservations] = useState([]);
  const [updatedFormulas, setUpdatedFormulas] = useState([]);
  const [filledObservations, setFilledObservations] = useState([]);
  const [template, setTemplate] = useState({});
  const [checkedInvestigations, setCheckedInvestigations] = useState({});

  const [observationData, setObservationData] = useState([]);

  const [isOrganismData, setIsOrganismData] = useState(false);
  const [organismObservations, setOrganismObservations] = useState([]);

  const [isPrefillCalculated, setIsPrefillCalculated] = useState(false);

  const defaultCheckedInvestigations = useMemo(() => {
    const defaultChecked = {};

    if (observationDataRedux && Object.keys(observationDataRedux).length > 0) {
      Object.entries(observationDataRedux).forEach(
        ([department, investigations]) => {
          Object.entries(investigations).forEach(
            ([investigation_name, observations]) => {
              const investigationKey = `${department}-${investigation_name}`;
              defaultChecked[investigationKey] = true;
            }
          );
        }
      );
    }

    return defaultChecked;
  }, [observationDataRedux]);

  const checkedInvestigationsRef = useRef({});
  const macInitializedKeysRef = useRef(new Set());

  useEffect(() => {
    macInitializedKeysRef.current.clear();
  }, [rowData?.appointment_id]);

  useEffect(() => {
    if (!isOpenObservationValue) {
      macInitializedKeysRef.current.clear();
      return;
    }

    macInitializedKeysRef.current.clear();
  }, [
    isOpenObservationValue,
    rowData?.appointment_id,
    rowData?.investigation_data?.investigation_id,
  ]);

  useEffect(() => {
    if (
      Object.keys(defaultCheckedInvestigations).length > 0 &&
      Object.keys(checkedInvestigations).length === 0
    ) {
      setCheckedInvestigations(defaultCheckedInvestigations);
      checkedInvestigationsRef.current = defaultCheckedInvestigations;
    }
  }, [defaultCheckedInvestigations, checkedInvestigations]);

  useEffect(() => {
    checkedInvestigationsRef.current = checkedInvestigations;
  }, [checkedInvestigations]);

  const handleCheckboxChange = (key) => {
    setCheckedInvestigations((prev) => {
      const next = {
        ...prev,
        [key]: !Boolean(prev[key]),
      };
      checkedInvestigationsRef.current = next;
      return next;
    });
  };

  const [
    numericValueAcceptableObservations,
    setNumericValueAcceptableObservations,
  ] = useState([]);

  const constructedObservationsRef = useRef([]);
  const filledObservationsRef = useRef([]);
  const observationDataReduxRef = useRef(observationDataRedux);

  useEffect(() => {
    filledObservationsRef.current = filledObservations;
  }, [filledObservations]);

  useEffect(() => {
    observationDataReduxRef.current = observationDataRedux;
  }, [observationDataRedux]);

  const auth = useSelector((state) => state.jwtAuthreducer);

  const stripHtmlTags = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  const onSuccess = () => {
    // const document = true;
    const message = rowData
      ? "Document updated successfully!"
      : "Document created successfully!";
    showAlert(message, "success");
    // refetchPayment();
    // refetchBussinessReport();
    // onClose(document);
    navigate("/patient-wise-search-report", {
      state: {
        restoreRowKey: getInvestigationRowKey(rowData),
      },
    });
  };

  const onError = (error) => {
    showAlert(getResultEntrySaveError(error), "danger");
  };

  const {
    data: listPayment,
    isLoading: isLoadingPayment,
    refetch: refetchPayment,
  } = useQuery(
    [
      "fetch-observation-result-entry",
      rowData?.appointment_id,
      investigationId,
    ],
    () =>
      fetchObservationResultEntryData({
        appointment_id: rowData?.appointment_id,
        investigation_id: investigationId,
      }),
    {
      staleTime: 0,
      select: (res) => {
        const response = res?.data?.data;
        if (response && response.length) {
          res.data.data = response.map((x, index) => ({
            ...x,
          }));
        }
        return res;
      },
      onError: (error) => {
        console.error("Error fetching payments:", error);
        Swal.fire({
          icon: "error",
          title: "Failed to Fetch Data",
          text:
            error?.response?.data?.detail ||
            "Something went wrong while fetching payment data.",
        });
      },
      enabled: !!rowData?.appointment_id && !!investigationId,
    }
  );

  // console.log("listPayment?.data?.observations", listPayment?.data);

  const hasAnyCulture = useMemo(() => {
    if (
      !observationDataRedux ||
      Object.keys(observationDataRedux).length === 0
    ) {
      return false;
    }

    return Object.entries(observationDataRedux).some(
      ([department, investigations]) =>
        Object.entries(investigations).some(
          ([investigation_name, observationWrapper]) =>
            observationWrapper?.investigation_detail?.is_culture === true
        )
    );
  }, [observationDataRedux]);

  const isInvestigationCulture = (department, investigationName) => {
    if (!observationDataRedux || !observationDataRedux[department]) {
      return false;
    }

    const investigation = observationDataRedux[department][investigationName];
    return investigation?.investigation_detail?.is_culture === true;
  };

  const {
    data: onlyOrganismData,
    isLoading: isLoadingOnlyOrganism,
    refetch: refetchOnlyOrganismDropDown,
  } = useQuery(["fetch-only-organism-data"], () => fetchOnlyOrganismData(), {
    staleTime: STALE_TIME,
    select: (res) => {
      const response = res?.data?.data;
      if (response && response.length) {
        res.data.data = response.map((x, index) => ({
          ...x,
        }));
      }
      return res;
    },
    onError: (error) => {
      console.error("Error fetching payments:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to Fetch Data",
        text:
          error?.response?.data?.detail ||
          "Something went wrong while fetching payment data.",
      });
    },
    enabled: hasAnyCulture,
  });

  // const {
  //   data: investigationObservationData,
  //   isLoading: isLoadingDropDown,
  //   refetch: refetchDropDown,
  // } = useQuery(
  //   ["fetch-observation-result-entry-drop-down", selectedInvestigationId],
  //   () =>
  //     fetchInvestigationObservationDropDown({
  //       investigation_id: selectedInvestigationId,
  //       req_help_data: true,
  //     }),
  //   {
  //     staleTime: STALE_TIME,
  //     enabled:
  //       !!selectedInvestigationId &&
  //       listPayment?.data?.header_details?.is_culture === true,
  //     onSuccess: (res) => {
  //       const response = res?.data?.data || [];

  //       setInvestigationDataMap((prevData) => ({
  //         ...prevData,
  //         [selectedInvestigationId]: response,
  //       }));
  //     },
  //     onError: (error) => {
  //       console.error("Error fetching payments:", error);
  //       Swal.fire({
  //         icon: "error",
  //         title: "Failed to Fetch Data",
  //         text:
  //           error?.response?.data?.detail ||
  //           "Something went wrong while fetching payment data.",
  //       });
  //     },
  //   }
  // );

  // useEffect(() => {
  //   selectedInvestigationIds.forEach((investigationId) => {
  //     if (listPayment?.data?.header_details?.is_culture) {
  //       fetchInvestigationObservationDropDown({
  //         investigation_id: investigationId,
  //         req_help_data: true,
  //       })
  //         .then((res) => {
  //           const response = res?.data?.data || [];

  //           // **Yahaan bhi merge karo na ki pura replace karo**
  //           setInvestigationDataMap((prevData) => ({
  //             ...prevData,
  //             [investigationId]: [
  //               // Pehle se existing data (agar koi hai)
  //               ...(prevData[investigationId] || []),
  //               // API response wala data (ya to replace karna ho to bas response hi rakh do)
  //               ...response,
  //             ],
  //           }));
  //         })
  //         .catch((error) => {
  //           console.error("Error fetching investigation data:", error);
  //           Swal.fire({
  //             icon: "error",
  //             title: "Failed to Fetch Data",
  //             text:
  //               error?.response?.data?.detail ||
  //               "Something went wrong while fetching investigation data.",
  //           });
  //         });
  //     }
  //   });
  // }, [selectedInvestigationIds, listPayment?.data?.header_details?.is_culture]);

  useEffect(() => {
    selectedInvestigationIds.forEach((investigationId) => {
      if (hasAnyCulture) {
        fetchInvestigationObservationDropDown({
          investigation_id: investigationId,
          req_help_data: true,
        })
          .then((res) => {
            const response = res?.data?.data || [];

            setInvestigationDataMap((prevData) => {
              const existingData = prevData[investigationId] || [];

              const combined = [...existingData, ...response];

              const uniqueData = combined.filter(
                (item, index, self) =>
                  index ===
                  self.findIndex(
                    (t) =>
                      t.investigation_id === item.investigation_id &&
                      t.observation_id === item.observation_id
                  )
              );

              return {
                ...prevData,
                [investigationId]: uniqueData,
              };
            });
          })
          .catch((error) => {
            console.error("Error fetching investigation data:", error);
            Swal.fire({
              icon: "error",
              title: "Failed to Fetch Data",
              text:
                error?.response?.data?.detail ||
                "Something went wrong while fetching investigation data.",
            });
          });
      }
    });
  }, [selectedInvestigationIds, hasAnyCulture]);

  const handleInvestigationChange = (event) => {
    const selectedId = event.target.value;

    setSelectedInvestigationId(selectedId);
    if (!selectedId) return;

    // console.log("selectedInvestigations", selectedInvestigations);

    if (selectedInvestigations.some((item) => item.id == selectedId)) {
      Swal.fire({
        icon: "warning",
        title: "Already Selected",
        text: "This investigation is already added!",
      });
      return;
    }

    const selectedOption = onlyOrganismData?.data?.data?.find(
      (item) => item.id == selectedId
    );

    if (selectedOption) {
      setSelectedInvestigations((prev) => [
        ...prev,
        {
          id: selectedOption.id,
          name: selectedOption.investigation_name,
        },
      ]);
      setSelectedInvestigationIds((prev) => [
        ...new Set([...prev, selectedOption.id]),
      ]);
    }
  };

  const handleRemoveInvestigation = (id) => {
    setSelectedInvestigations((prev) => prev.filter((inv) => inv.id !== id));
    setInvestigationDataMap((prevData) => {
      const updatedData = { ...prevData };
      delete updatedData[id];
      return updatedData;
    });
  };

  const inputRefs = useRef([]);
  const inputRefsDrug = useRef([]);
  const lastFocusedInput = useRef(null);
  const isNavigatingWithinTableRef = useRef(false);
  const editingObservationIdRef = useRef(null);
  const observationLoadKeyRef = useRef(null);
  const pendingObservationValuesRef = useRef(new Map());
  const isOrganismCultureActiveRef = useRef(false);
  const organismDataSignatureRef = useRef("");

  const flushAllPendingObservationValues = useCallback(() => {
    pendingObservationValuesRef.current.forEach((entry, obsId) => {
      dispatch(
        updateResultEntryObservation(
          entry.department,
          entry.investigation,
          obsId,
          entry.value
        )
      );
      pendingObservationValuesRef.current.set(String(obsId), {
        ...entry,
        flushed: true,
      });
    });
  }, [dispatch]);

  useEffect(() => {
    if (!isOpenObservationValue) {
      observationLoadKeyRef.current = null;
      pendingObservationValuesRef.current.clear();
      isOrganismCultureActiveRef.current = false;
      organismDataSignatureRef.current = "";
      return;
    }

    const departmentData =
      listPayment?.data?.observations?.department_investigation;
    if (!departmentData || !rowData?.appointment_id || !investigationId) {
      return;
    }

    const loadKey = `${rowData.appointment_id}-${investigationId}`;
    if (observationLoadKeyRef.current === loadKey) {
      return;
    }

    observationLoadKeyRef.current = loadKey;
    pendingObservationValuesRef.current.clear();
    isOrganismCultureActiveRef.current = false;
    organismDataSignatureRef.current = "";
    inputRefs.current = [];
    setConstructedObservations([]);
    constructedObservationsRef.current = [];
    setUpdatedFormulas([]);
    dispatch(resetResultEntryObservations());
    dispatch(setResultEntryObservations(departmentData));
  }, [
    isOpenObservationValue,
    rowData?.appointment_id,
    investigationId,
    listPayment?.data?.observations?.department_investigation,
    dispatch,
  ]);

  useEffect(() => {
    if (!isOpenObservationValue) {
      return;
    }

    const handlePointerDownOutsideTable = (event) => {
      if (isTableObservationInput(inputRefs, event.target)) {
        return;
      }
      if (pendingObservationValuesRef.current.size === 0) {
        return;
      }
      flushAllPendingObservationValues();
    };

    document.addEventListener(
      "mousedown",
      handlePointerDownOutsideTable,
      true
    );
    return () => {
      document.removeEventListener(
        "mousedown",
        handlePointerDownOutsideTable,
        true
      );
    };
  }, [isOpenObservationValue, flushAllPendingObservationValues]);

  // const dataRef = useRef(null);

  // useEffect(() => {
  //   console.log("trigger");

  //   const currentData =
  //     listPayment?.data?.observations?.department_investigation;
  //   const currentDataString = JSON.stringify(currentData);

  //   if (
  //     isOpenObservationValue &&
  //     currentData &&
  //     currentDataString !== dataRef.current
  //   ) {
  //     dispatch(setResultEntryObservations(currentData));
  //     dataRef.current = currentDataString;
  //   }
  // }, [
  //   isOpenObservationValue,
  //   listPayment?.data?.observations?.department_investigation,
  // ]);

  // useEffect(() => {
  //   if (!isOpenObservationValue) {
  //     dataRef.current = null;
  //   }
  // }, [isOpenObservationValue]);

  // useEffect(() => {
  //   if (
  //     listPayment?.data?.observation_organism &&
  //     listPayment.data.observation_organism.length > 0
  //   ) {
  //     setIsOrganismData(true);
  //     setOrganismObservations(listPayment.data.observation_organism);

  //     const organismInvestigationIds =
  //       listPayment.data.observation_organism.map(
  //         (item) => item.investigation_id
  //       );

  //     const uniqueInvestigationIds = [...new Set(organismInvestigationIds)];

  //     setSelectedInvestigationIds(uniqueInvestigationIds);

  //     uniqueInvestigationIds.forEach((investigationId) => {
  //       const investigationData = listPayment.data.observation_organism.filter(
  //         (item) => item.investigation_id === investigationId
  //       );

  //       const investigationInfo = onlyOrganismData?.data?.data?.find(
  //         (item) => item.id === investigationId
  //       );

  //       if (investigationInfo) {
  //         setSelectedInvestigations((prev) => {
  //           const exists = prev.some((inv) => inv.id === investigationId);
  //           if (!exists) {
  //             return [
  //               ...prev,
  //               {
  //                 id: investigationId,
  //                 name: investigationInfo.investigation_name,
  //               },
  //             ];
  //           }
  //           return prev;
  //         });

  //         // **Merge kar rahe hain previous data ke sath**
  //         setInvestigationDataMap((prev) => ({
  //           ...prev,
  //           // Agar pehle se data hai to use bhi merge kar do
  //           [investigationId]: [
  //             ...(prev[investigationId] || []),
  //             ...investigationData,
  //           ],
  //         }));
  //       }
  //     });
  //   } else {
  //     setIsOrganismData(false);
  //     setOrganismObservations([]);
  //     setSelectedInvestigationIds([]);
  //     setInvestigationDataMap({}); // Clear bhi kar sakte ho agar data nahi hai
  //   }
  // }, [listPayment?.data?.observation_organism]);

  useEffect(() => {
    let observationOrganismData = [];

    Object.entries(observationDataRedux).forEach(
      ([department, investigations]) => {
        Object.entries(investigations).forEach(
          ([investigation_name, observationWrapper]) => {
            if (
              observationWrapper?.investigation_organism &&
              observationWrapper.investigation_organism.length > 0
            ) {
              observationOrganismData = [
                ...observationOrganismData,
                ...observationWrapper.investigation_organism,
              ];
            }
          }
        );
      }
    );

    const organismSignature = observationOrganismData
      .map((item) => `${item.investigation_id}-${item.observation_id}`)
      .join("|");

    if (observationOrganismData.length > 0) {
      if (organismSignature === organismDataSignatureRef.current) {
        return;
      }
      organismDataSignatureRef.current = organismSignature;
      isOrganismCultureActiveRef.current = true;

      setIsOrganismData(true);
      setOrganismObservations(observationOrganismData);

      const organismInvestigationIds = observationOrganismData.map(
        (item) => item.investigation_id
      );

      const uniqueInvestigationIds = [...new Set(organismInvestigationIds)];

      setSelectedInvestigationIds(uniqueInvestigationIds);

      let allObservationData = [];

      uniqueInvestigationIds.forEach((investigationId) => {
        const investigationData = observationOrganismData.filter(
          (item) => item.investigation_id === investigationId
        );

        const investigationInfo = onlyOrganismData?.data?.data?.find(
          (item) => item.id === investigationId
        );

        if (investigationInfo) {
          setSelectedInvestigations((prev) => {
            const exists = prev.some((inv) => inv.id === investigationId);
            if (!exists) {
              return [
                ...prev,
                {
                  id: investigationId,
                  name: investigationInfo.investigation_name,
                },
              ];
            }
            return prev;
          });

          setInvestigationDataMap((prev) => {
            const existingData = prev[investigationId] || [];
            const combined = [...existingData, ...investigationData];

            const uniqueData = combined.filter(
              (item, index, self) =>
                index ===
                self.findIndex(
                  (t) =>
                    t.investigation_id === item.investigation_id &&
                    t.observation_id === item.observation_id
                )
            );

            const currentObservationData = uniqueData
              .filter(
                (item) =>
                  (item.observation_value !== undefined &&
                    item.observation_value !== null &&
                    String(item.observation_value).trim() !== "") ||
                  (item.observation_value_mac_reading !== undefined &&
                    item.observation_value_mac_reading !== null &&
                    String(item.observation_value_mac_reading).trim() !== "")
              )
              .map((item) => ({
                id: item.observation_id,
                parent_investigation_id: item.investigation_id,
                observation_value: item.observation_value || "",
                observation_value_mac_reading:
                  item.observation_value_mac_reading || "",
              }));

            allObservationData = [
              ...allObservationData,
              ...currentObservationData,
            ];

            allObservationData = allObservationData.filter(
              (item, index, self) =>
                index ===
                self.findIndex(
                  (t) =>
                    t.id === item.id &&
                    t.parent_investigation_id === item.parent_investigation_id
                )
            );

            setObservationData(allObservationData);

            return {
              ...prev,
              [investigationId]: uniqueData,
            };
          });
        }
      });
      return;
    }

    if (isOrganismCultureActiveRef.current) {
      isOrganismCultureActiveRef.current = false;
      organismDataSignatureRef.current = "";
      setIsOrganismData(false);
      setOrganismObservations([]);
      setSelectedInvestigationIds([]);
      setInvestigationDataMap({});
    }
  }, [observationDataRedux]);

  // console.log(
  //   "listPayment?.data?.observation_organism",
  //   listPayment?.data?.observation_organism
  // );
  // console.log("invesfadsfdasfdsf", investigationDataMap);

  const {
    data: reportRoleData,
    isLoading,
    refetch: refetchReportRole,
  } = useQuery(
    ["fetch-report-role", jwtAuthreducer.user.employee_code_id],
    () =>
      fetchReportRoleById({
        employee_code_id: jwtAuthreducer.user.employee_code_id,
      }),
    {
      keepPreviousData: false,
      staleTime: STALE_TIME,
      select: (res) => {
        return res;
      },
      onError: (res) => {
        console.error("Error fetching areas:", res);
      },
    }
  );

  // console.log("reportRoleData", reportRoleData?.data);

  const { mutate: addPatientLabResultEntryMutate, isLoading: isLoadingAdd } =
    useMutation({
      mutationFn: createPatientLabResultEntry,
      onSuccess,
      onError,
    });

  const updateRow = useCallback((originalRow, rowId, field, value) => {
    setUpdatedRows((prev) => {
      const newUpdatedRows = {
        ...prev,
        [rowId]: {
          ...originalRow,
          [field]: value,
        },
      };
      setIsSaveEnabled(Object.keys(newUpdatedRows).length > 0);
      return newUpdatedRows;
    });
  }, []);

  // const handleSave = () => {
  //   if (
  //     !observationDataRedux ||
  //     Object.keys(observationDataRedux).length === 0
  //   ) {
  //     console.warn("No data available to save.");
  //     return;
  //   }

  //   if (status === "APPROVED" && !filterValue && !doctorId) {
  //     return;
  //   }

  //   const groupedData = {};
  //   let patientNameId = 0;
  //   let appointmentInvestigationId = 0;

  //   Object.entries(observationDataRedux).forEach(
  //     ([department, investigations]) => {
  //       Object.entries(investigations).forEach(
  //         ([investigation_name, observations]) => {
  //           const key = `${department}-${investigation_name}`;
  //           const currentComment = commentsMap[key] || "";
  //           const currentCommentName = commentNamesMap[key] || null;

  //           const investigationKey = `${department}-${investigation_name}`;
  //           const currentTemplate = template[investigationKey] || {};

  //           const allFilled = observations.every(
  //             (obs) =>
  //               obs.header === true ||
  //               (obs.observation_value !== undefined &&
  //                 obs.observation_value !== null &&
  //                 obs.observation_value !== "")
  //           );
  //           const currentStatus = allFilled ? "RESULT_DONE" : "INCOMPLETE";

  //           observations.forEach((obs) => {
  //             if (!currentTemplate?.template_id && !obs.observation_value) {
  //               return;
  //             }

  //             if (obs.patient_details?.id) {
  //               patientNameId = obs.patient_details.id;
  //             }

  //             if (obs.appointment_investigation_id) {
  //               appointmentInvestigationId = obs.appointment_investigation_id;
  //             }

  //             const appointment_id = obs.appointment_id || 0;
  //             const investigation_id = obs.investigation_id || 0;

  //             if (!groupedData[appointment_id]) {
  //               groupedData[appointment_id] = [];
  //             }

  //             let investigationEntry = groupedData[appointment_id].find(
  //               (entry) => entry.investigation_id === investigation_id
  //             );

  //             if (!investigationEntry) {
  //               investigationEntry = {
  //                 investigation_id,
  //                 appointment_id,
  //                 observation: [],
  //                 observation_organism: [],
  //               };
  //               groupedData[appointment_id].push(investigationEntry);
  //             }

  //             investigationEntry.observation.push({
  //               observation_id: obs.observation_id,
  //               gender: obs.patient_details?.gender,
  //               observation_value: String(obs.observation_value),
  //               status: status === "SAVE" ? currentStatus : status,
  //               min_reading: obs.min_reading,
  //               max_reading: obs.max_reading,
  //               min_critical: obs.min_critical,
  //               max_critical: obs.max_critical,
  //               unit: obs.unit,
  //               barcode_no: obs.barcode_no,
  //               doctor_id: filterValue,
  //               comments: stripHtmlTags(currentComment),
  //               comment_id: currentCommentName?.comment_id,
  //               modified_by: auth?.user?.employee_code_id,
  //               template_id: currentTemplate?.template_id,
  //               template_description: currentTemplate?.template_description,
  //               display_reading: obs.display_reading,
  //             });
  //           });
  //         }
  //       );
  //     }
  //   );

  //   if (observationData && observationData.length > 0) {
  //     Object.values(groupedData)
  //       .flat()
  //       .forEach((investigationEntry) => {
  //         observationData.forEach((obsData) => {
  //           if (
  //             obsData.observation_value !== undefined &&
  //             obsData.observation_value !== null &&
  //             String(obsData.observation_value).trim() !== ""
  //             // &&
  //             // obsData.observation_value_mac_reading !== undefined &&
  //             // obsData.observation_value_mac_reading !== null &&
  //             // String(obsData.observation_value_mac_reading).trim() !== ""
  //           ) {
  //             investigationEntry.observation_organism.push({
  //               patient_name_id: patientNameId,
  //               observation_id: obsData.id,
  //               investigation_id: obsData?.parent_investigation_id,
  //               parent_investigation_id: investigationEntry.investigation_id,
  //               appointment_id: investigationEntry.appointment_id,
  //               franchise_id: Number(localStorage.getItem("franchise_id")),
  //               zone_id: Number(zoneLocalStorageId),
  //               appointment_investigation_id: appointmentInvestigationId,
  //               gender: "",
  //               observation_value: String(obsData.observation_value),
  //               observation_value_mac_reading: String(
  //                 obsData.observation_value_mac_reading
  //               ),
  //               min_reading: "",
  //               max_reading: "",
  //               min_critical: "",
  //               max_critical: "",
  //               unit: "",
  //               is_active: true,
  //               created_by: auth?.user?.employee_code_id || 0,
  //             });
  //           }
  //         });
  //       });
  //   }

  //   const payload = {
  //     data: Object.values(groupedData).flat(),
  //   };

  //   if (payload.data.length > 0) {
  //     addPatientLabResultEntryMutate(payload, {
  //       onSuccess,
  //       onError,
  //     });
  //   }
  // };

  const handleSave = () => {
    const pendingSnapshot = new Map(pendingObservationValuesRef.current);
    flushAllPendingObservationValues();

    const getObservationValueForSave = (obs) => {
      const pending = pendingSnapshot.get(String(obs.observation_id));
      if (pending) {
        return pending.value;
      }
      return obs.observation_value;
    };

    if (
      !observationDataRedux ||
      Object.keys(observationDataRedux).length === 0
    ) {
      console.warn("No data available to save.");
      return;
    }

    if (status === "APPROVED" && !filterValue && !doctorId) {
      return;
    }

    // CBC validation: Lymphocyte(45) + Eosinophil(46) + Monocyte(47) + Basophil(48) must not exceed 100 (checked tests only)
    const CBC_DEPENDENT_IDS = [45, 46, 47, 48];
    let cbcSum = 0;
    let cbcFound = false;
    Object.entries(observationDataRedux).forEach(([department, investigations]) => {
      Object.entries(investigations).forEach(
        ([investigation_name, observationWrapper]) => {
          const investigationKey = `${department}-${investigation_name}`;
          if (checkedInvestigations[investigationKey] !== true) {
            return;
          }
          const observations = observationWrapper?.observations || [];
          const cbcObs = observations.filter((obs) =>
            CBC_DEPENDENT_IDS.includes(+obs.observation_id)
          );
          if (cbcObs.length > 0) {
            cbcFound = true;
            cbcObs.forEach((obs) => {
              cbcSum += parseFloat(getObservationValueForSave(obs)) || 0;
            });
          }
        }
      );
    });
    if (cbcFound && cbcSum > 100) {
      showAlert(
        `CBC values invalid: Lymphocyte + Eosinophil + Monocyte + Basophil = ${cbcSum.toFixed(2)}, which exceeds 100. Please correct the values.`,
        "danger"
      );
      return;
    }

    const groupedData = {};
    let patientNameId = 0;
    let appointmentInvestigationId = 0;

    Object.entries(observationDataRedux).forEach(
      ([department, investigations]) => {
        Object.entries(investigations).forEach(
          ([investigation_name, observationWrapper]) => {
            // Updated to handle new structure with observationWrapper
            const observations = observationWrapper?.observations || [];

            const investigationKey = `${department}-${investigation_name}`;
            if (checkedInvestigations[investigationKey] !== true) {
              return;
            }

            const currentComment = commentsMap[investigationKey] || "";
            const currentCommentName = commentNamesMap[investigationKey] || null;

            const currentTemplate = template[investigationKey] || {};

            const allFilled = observations.every((obs) => {
              if (obs.header === true) {
                return true;
              }
              const value = getObservationValueForSave(obs);
              return value !== undefined && value !== null && value !== "";
            });
            const currentStatus = allFilled ? "RESULT_DONE" : "INCOMPLETE";

            observations.forEach((obs) => {
              const observationValue = getObservationValueForSave(obs);
              if (!currentTemplate?.template_id && !observationValue) {
                return;
              }

              if (obs.patient_details?.id) {
                patientNameId = obs.patient_details.id;
              }

              if (obs.appointment_investigation_id) {
                appointmentInvestigationId = obs.appointment_investigation_id;
              }

              const appointment_id = obs.appointment_id || 0;
              // Updated to use investigation_details.id from new structure
              const investigation_id =
                obs.investigation_details?.id || obs.investigation_id || 0;

              if (!groupedData[appointment_id]) {
                groupedData[appointment_id] = [];
              }

              let investigationEntry = groupedData[appointment_id].find(
                (entry) => entry.investigation_id === investigation_id
              );

              if (!investigationEntry) {
                investigationEntry = {
                  investigation_id,
                  appointment_id,
                  observation: [],
                  observation_organism: [],
                };
                groupedData[appointment_id].push(investigationEntry);
              }

              investigationEntry.observation.push({
                observation_id: obs.observation_id,
                gender: obs.patient_details?.gender,
                observation_value: String(observationValue),
                status: status === "SAVE" ? currentStatus : status,
                min_reading: obs.min_reading,
                max_reading: obs.max_reading,
                min_critical: obs.min_critical,
                max_critical: obs.max_critical,
                unit: obs.unit,
                barcode_no: obs.barcode_no,
                doctor_id: filterValue,
                comments: currentComment,
                comment_id: currentCommentName?.comment_id,
                modified_by: auth?.user?.employee_code_id,
                template_id: currentTemplate?.template_id,
                template_description: currentTemplate?.template_description,
                display_reading: obs.display_reading,
              });
            });
          }
        );
      }
    );
    // console.log("observationData", observationData);
    if (observationData && observationData.length > 0) {
      Object.values(groupedData)
        .flat()
        .forEach((investigationEntry) => {
          observationData.forEach((obsData) => {
            if (obsData.parent_investigation_id !== investigationEntry.investigation_id) {
              return;
            }
            if (
              obsData.observation_value !== undefined &&
              obsData.observation_value !== null &&
              String(obsData.observation_value).trim() !== ""
            ) {
              investigationEntry.observation_organism.push({
                patient_name_id: patientNameId,
                observation_id: obsData.id,
                investigation_id: obsData?.parent_investigation_id,
                parent_investigation_id: investigationEntry.investigation_id,
                appointment_id: investigationEntry.appointment_id,
                franchise_id: Number(localStorage.getItem("franchise_id")),
                zone_id: zoneLocalStorageId(),
                appointment_investigation_id: appointmentInvestigationId,
                gender: "",
                observation_value: String(obsData.observation_value ?? ""),
                observation_value_mac_reading: String(
                  obsData.observation_value_mac_reading ?? ""
                ),
                min_reading: "",
                max_reading: "",
                min_critical: "",
                max_critical: "",
                unit: "",
                is_active: true,
                created_by: auth?.user?.employee_code_id || 0,
              });
            }
          });
        });
    }

    const payload = {
      data: Object.values(groupedData).flat(),
    };

    if (payload.data.length > 0) {
      addPatientLabResultEntryMutate(payload, {
        onSuccess,
        onError,
      });
    }
  };

  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const handleValueChange = useCallback(
    (
      value,
      department,
      investigation,
      obsId,
      isUserInput = false,
      flushToRedux = false
    ) => {
      const currentConstructedObservations = constructedObservationsRef.current;
      const shouldFlushToRedux = flushToRedux || !isUserInput;

      setFilledObservations((prev) => {
        const obsIndex = prev.findIndex((o) => +o.id === +obsId);
        const formulaIndex = currentConstructedObservations.findIndex(
          (o) => +o.id === +obsId
        );
        const numericValue = value === "" || value === null ? null : Number(value);
        const obs = {
          id: obsId,
          value:
            numericValue !== null && !Number.isNaN(numericValue)
              ? numericValue
              : value,
        };
        if (isUserInput || (formulaIndex > -1 && +obsId !== 44)) {
          obs.isDirectChange = true;
        }

        if (obsIndex > -1) {
          if (!value && value !== 0) {
            return prev.filter((_, index) => index !== obsIndex);
          }
          return prev.map((o, index) => (index === obsIndex ? obs : o));
        }
        return [...prev, obs];
      });

      if (!shouldFlushToRedux) {
        if (value === "" && value !== 0) {
          pendingObservationValuesRef.current.delete(String(obsId));
        } else {
          pendingObservationValuesRef.current.set(String(obsId), {
            value,
            department,
            investigation,
            flushed: false,
          });
        }
        return;
      }

      if (value === "" && value !== 0) {
        pendingObservationValuesRef.current.delete(String(obsId));
      } else {
        pendingObservationValuesRef.current.set(String(obsId), {
          value,
          department,
          investigation,
          flushed: true,
        });
      }
      dispatch(
        updateResultEntryObservation(department, investigation, obsId, value)
      );
    },
    [dispatch]
  );

  // useEffect(() => {
  //   if (
  //     observationDataRedux &&
  //     Object.keys(observationDataRedux).length > 0 &&
  //     !isPrefillCalculated
  //   ) {
  //     console.log("🔥 TRIGGERING PREFILL CALCULATIONS...");

  //     Object.entries(observationDataRedux).forEach(
  //       ([department, investigations]) => {
  //         Object.entries(investigations).forEach(
  //           ([investigation_name, observationWrapper]) => {
  //             const observations = observationWrapper?.observations || [];

  //             observations.forEach((obs) => {
  //               if (
  //                 obs.observation_value !== undefined &&
  //                 obs.observation_value !== "" &&
  //                 obs.observation_value !== null
  //               ) {
  //                 console.log(
  //                   `🚀 Processing prefilled value: ${obs.observation_value} for obs ID: ${obs.observation_id}`
  //                 );

  //                 handleValueChange(
  //                   obs.observation_value,
  //                   department,
  //                   investigation_name,
  //                   obs.observation_id
  //                 );
  //               }
  //             });
  //           }
  //         );
  //       }
  //     );

  //     setIsPrefillCalculated(true);
  //   }
  // }, [observationDataRedux, isPrefillCalculated, handleValueChange]);

  // useEffect(() => {
  //   if (observationDataRedux && Object.keys(observationDataRedux).length > 0) {
  //     setIsPrefillCalculated(false);
  //   }
  // }, [observationDataRedux]);

  useEffect(() => {
    if (!constructedObservations.length) {
      const constructedObs = constructFormulaFieldsFromObservations({
        department_investigation: observationDataRedux,
      });
      setConstructedObservations(constructedObs);
      constructedObservationsRef.current = constructedObs;
    }
  }, [observationDataRedux]);

  useEffect(() => {
    const numericValueObservations = [];
    for (const obs of constructedObservations) {
      const constructedObervationIds = obs.fields.map(
        (f) => +f.replace(/[#@]/g, "")
      );
      numericValueObservations.push(...constructedObervationIds);
    }
    const uniqueNumericValueObservations = [
      ...new Set(numericValueObservations),
    ];
    setNumericValueAcceptableObservations(uniqueNumericValueObservations);
  }, [constructedObservations]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const headerDetails = listPayment?.data?.header_details;
      let ageForFormula = null;
      if (headerDetails?.age !== undefined && headerDetails?.age !== null) {
        const ageRaw = Number(headerDetails.age);
        if (Number.isFinite(ageRaw)) {
          const ageInYears = ageRaw > 150 ? ageRaw / 365.25 : ageRaw;
          if (ageInYears > 0 && ageInYears <= 120) ageForFormula = ageInYears;
        }
      }
      const formulas = constructFormulasForFilledValues(
        filledObservationsRef.current,
        constructedObservations,
        ageForFormula,
        observationDataReduxRef.current,
        headerDetails?.gender
      );
      let removedFormulas = [];

      setUpdatedFormulas((prev) => {
        const newIds = new Set(formulas.map((f) => f.id));
        removedFormulas = prev.filter((f) => !newIds.has(f.id));
        return formulasArrayEqual(prev, formulas) ? prev : formulas;
      });

      removedFormulas.forEach(({ department, investigation, id }) => {
        if (hasManualObservationOverride(filledObservationsRef.current, id)) {
          return;
        }
        const row = getObservationRowFromGrid(
          id,
          observationDataReduxRef.current
        );
        if (
          row?.observation_value === "" ||
          row?.observation_value === undefined ||
          row?.observation_value === null
        ) {
          return;
        }
        dispatch(
          updateResultEntryObservation(department, investigation, id, "")
        );
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [
    filledObservations,
    constructedObservations,
    observationDataRedux,
    listPayment?.data?.header_details,
    dispatch,
  ]);

  // console.table("constructedObservations", constructedObservations);

  useEffect(() => {
    if (!updatedFormulas.length) {
      return;
    }

    updatedFormulas.forEach((obs) => {
      const { department, investigation, id, observationValue } = obs;

      if (+editingObservationIdRef.current === +id) {
        return;
      }

      if (hasManualObservationOverride(filledObservationsRef.current, id)) {
        return;
      }

      let value = parseFloat(Number(observationValue));
      let isValueRemoved = false;
      if (isNaN(value)) isValueRemoved = true;

      const reduxRow = getObservationRowFromGrid(
        id,
        observationDataReduxRef.current
      );

      const formattedValue = isEgfrObservationName(reduxRow?.observation_name)
        ? Math.round(value)
        : +(value < 0 ? value * -1 : value).toFixed(2);

      if (
        !isValueRemoved &&
        observationValuesEquivalent(
          reduxRow?.observation_value,
          String(formattedValue)
        )
      ) {
        return;
      }

      setFilledObservations((prev) => {
        const obsIndex = prev.findIndex((o) => +o.id === +id);
        const obs = { id, value: formattedValue };
        let updatedFilledObservations;
        if (obsIndex > -1) {
          // console.log(
          //   `Condition (formattedValue === undefined - ${formattedValue}) >>>`,
          //   formattedValue === undefined
          // );
          // console.log(
          //   `Condition (typeof formattedValue !== "number" - ${formattedValue}) >>>`,
          //   typeof formattedValue !== "number"
          // );
          if (isValueRemoved) {
            updatedFilledObservations = prev.filter(
              (_, index) => index !== obsIndex
            );
          } else {
            updatedFilledObservations = prev.map((o, index) => {
              // return index === obsIndex ? obs : o;
              if (index === obsIndex) {
                if (o?.isDirectChange) {
                  return o;
                }
                return obs;
              }
              return o;
            });
          }
        } else {
          updatedFilledObservations = [...prev, obs];
        }

        if (JSON.stringify(prev) === JSON.stringify(updatedFilledObservations))
          return prev;
        return updatedFilledObservations;
      });

      dispatch(
        updateResultEntryObservation(
          department,
          investigation,
          id,
          isValueRemoved ? "" : String(formattedValue)
        )
      );
    });
  }, [updatedFormulas, dispatch]);

  // useEffect(() => {
  //   if (observationDataRedux && Object.keys(observationDataRedux).length > 0) {
  //     const newCheckedInvestigations = {};

  //     Object.entries(observationDataRedux).forEach(
  //       ([department, investigations]) => {
  //         Object.entries(investigations).forEach(
  //           ([investigation_name, observations]) => {
  //             const investigationKey = `${department}-${investigation_name}`;
  //             newCheckedInvestigations[investigationKey] = true;
  //           }
  //         );
  //       }
  //     );

  //     // console.log(
  //     //   "Setting checked investigations from Redux:",
  //     //   newCheckedInvestigations
  //     // );
  //     setCheckedInvestigations(newCheckedInvestigations);
  //   }
  // }, [observationDataRedux]);

  const add_observation_columns = useMemo(() => {
    return ADD_OBSERVATION_COLUMNS_DATA(
      handleValueChange,
      inputRefs,
      numericValueAcceptableObservations,
      checkedInvestigationsRef,
      lastFocusedInput,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      editingObservationIdRef,
      null,
      macInitializedKeysRef,
      pendingObservationValuesRef,
      isNavigatingWithinTableRef
    );
  }, [
    inputRefs,
    lastFocusedInput,
    handleValueChange,
    numericValueAcceptableObservations,
  ]);

  const handleValue = (
    rowId,
    newValue,
    parent_investigation_id,
    fieldType = "observation_value"
  ) => {
    setObservationData((prevData) => {
      if (!prevData || prevData.length === 0) {
        const newRow = {
          id: rowId,
          parent_investigation_id: parent_investigation_id,
          [fieldType]: newValue,
        };
        return [newRow];
      }

      const rowExists = prevData.some(
        (row) => String(row.id) === String(rowId)
      );

      if (rowExists) {
        return prevData.map((row) =>
          String(row.id) === String(rowId)
            ? {
                ...row,
                [fieldType]: newValue,
                parent_investigation_id: parent_investigation_id, // ✅ FIXED HERE
              }
            : row
        );
      } else {
        const newRow = {
          id: rowId,
          parent_investigation_id: parent_investigation_id,
          [fieldType]: newValue,
        };
        return [...prevData, newRow];
      }
    });
  };

  const investigation_observation_columns = useMemo(() => {
    return INVESTIGATION_OBSERVATION_COLUMNS_DATA(handleValue, inputRefs);
  }, [inputRefs]);

  const handleCommentChange = (departmentName, investigationName, value) => {
    const key = `${departmentName}-${investigationName}`;
    setCommentsMap((prevMap) => ({
      ...prevMap,
      [key]: value,
    }));
  };

  // Handle comment name selection for a specific investigation
  const handleCommentNameChange = (
    departmentName,
    investigationName,
    selectedCommentName
  ) => {
    const key = `${departmentName}-${investigationName}`;

    if (selectedCommentName === null) {
      // Handle clearing the dropdown
      setCommentNamesMap((prevMap) => {
        const newMap = { ...prevMap };
        delete newMap[key];
        return newMap;
      });

      setCommentsMap((prevMap) => {
        const newMap = { ...prevMap };
        delete newMap[key];
        return newMap;
      });
      return;
    }

    setCommentNamesMap((prevMap) => ({
      ...prevMap,
      [key]: selectedCommentName,
    }));

    if (selectedCommentName?.comments) {
      setCommentsMap((prevMap) => ({
        ...prevMap,
        [key]: selectedCommentName.comments,
      }));
    }
  };

  useEffect(() => {
    if (!observationDataRedux || Object.keys(observationDataRedux).length === 0)
      return;

    const newCommentsMap = {};
    const newCommentNamesMap = {};
    const extractedDoctors = {};
    const newTemplateMap = {};

    Object.entries(observationDataRedux).forEach(
      ([department, investigations]) => {
        Object.entries(investigations).forEach(
          ([investigation_name, observationWrapper]) => {
            const key = `${department}-${investigation_name}`;
            const observations = observationWrapper?.observations || [];

            if (observations && observations.length > 0) {
              newCommentsMap[key] = getInvestigationComment(observationWrapper);

              if (observationWrapper?.comment_obj) {
                newCommentNamesMap[key] = {
                  comment_id: observationWrapper.comment_obj.id,
                  comment_name: observationWrapper.comment_obj.comment_name,
                  comments: observationWrapper.comment_obj.comments,
                };
              } else {
                const obsWithCommentId = observations.find(
                  (obs) => obs?.comment_id
                );
                if (obsWithCommentId) {
                  newCommentNamesMap[key] = {
                    comment_id: obsWithCommentId.comment_id,
                    comment_name: obsWithCommentId.comment_name || "",
                    comments: obsWithCommentId.comments || "",
                  };
                }
              }

              observations.forEach((obs) => {
                if (obs?.doctor_obj?.id) {
                  extractedDoctors[key] = obs.doctor_obj.id;
                }
              });

              const template_id =
                observationWrapper?.template_id || observations[0]?.template_id;
              const template_description =
                observationWrapper?.template_description ||
                observations[0]?.template_description ||
                "";

              if (template_id || template_description) {
                newTemplateMap[key] = {
                  template_id,
                  template_description,
                };
              }
            }
          }
        );
      }
    );

    setCommentsMap(newCommentsMap);
    setCommentNamesMap(newCommentNamesMap);
    setTemplate(newTemplateMap);

    const firstDoctorId = Object.values(extractedDoctors)[0];
    if (firstDoctorId) {
      setdoctorId(firstDoctorId);
    }
  }, [observationDataRedux]);

  const stripHtml = (html) => {
    if (!html || typeof html !== "string") return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  if (isLoadingPayment) {
    return <Loader isLoading={isLoadingPayment} />;
  }

  return (
    <>
      <div style={{ position: "relative" }}>
        {(isLoadingPayment || isLoading || isLoadingAdd) && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
            }}
          >
            <Loader isLoading={true} />
          </div>
        )}

        <TitleComponent headerDetails={listPayment?.data?.header_details} />
        <div className="d-flex justify-content-between align-items-center mb-1">
          <InvestigationResults
            extraInvestigations={listPayment?.data?.extra_investigation}
            observations={listPayment?.data?.observations}
            setInvestigationId={setInvestigationId}
          />
        </div>

        {observationDataRedux &&
          Object.keys(observationDataRedux).length > 0 &&
          Object.entries(observationDataRedux).map(
            ([department, investigations]) =>
              Object.entries(investigations).map(
                ([investigation_name, observationWrapper], index) => {
                  const investigationKey = `${department}-${investigation_name}`;
                  const observations = observationWrapper?.observations || [];
                  const investigationIds =
                    observations[0]?.investigation_details?.id || null;
                  const investigationBarcode =
                    observations.find(
                      (obs) =>
                        obs?.barcode_no !== null &&
                        obs?.barcode_no !== undefined &&
                        String(obs.barcode_no).trim() !== ""
                    )?.barcode_no || "";

                  // console.log("investigation_name", investigation_name);
                  // console.log("obervations", observations);
                  return (
                    <div
                      key={`${department}-${investigation_name}`}
                      style={{ marginBottom: 20 }}
                    >
                      <div
                        className="d-flex justify-content-between align-items-center p-2 mb-2"
                        style={{ background: "#afeeee" }}
                      >
                        <p style={{ fontSize: "16px", fontWeight: "bold" }}>
                          <input
                            type="checkbox"
                            checked={Boolean(
                              checkedInvestigations[investigationKey]
                            )}
                            onChange={() =>
                              handleCheckboxChange(investigationKey)
                            }
                            style={{ marginRight: 8 }}
                          />
                          {index + 1}. {investigation_name}
                        </p>
                        <div className="d-flex align-items-center gap-3">
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              minWidth: "260px",
                              textAlign: "left",
                              paddingLeft: "6px",
                            }}
                          >
                            Barcode: {investigationBarcode || "-"}
                          </span>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleDocuments}
                          >
                            Add Attachment
                          </button>
                        </div>
                        {documentInfo?.data?.file_name && (
                          <span
                            style={{
                              fontSize: "15px",
                              fontWeight: "bold",
                              padding: "5px",
                              marginLeft: "10px",
                            }}
                          >
                            {documentInfo?.data?.file_name}
                          </span>
                        )}
                      </div>
                      {observations?.map((observation, obsIndex) => {
                        const reportType =
                          observation?.investigation_details?.report_type;

                        return (
                          <div key={obsIndex} style={{ marginBottom: "10px" }}>
                            {reportType === "Path RichText" ? (
                              <>
                                <div className="col-md-4">
                                  <h5
                                    style={{
                                      fontSize: "13px",
                                      marginBottom: "5px",
                                    }}
                                  >
                                    Investigation Template
                                  </h5>
                                  <TemplateDropDown
                                    investigationId={investigationIds}
                                    setFilterValue={(val) =>
                                      setTemplate((prev) => ({
                                        ...prev,
                                        [investigationKey]: {
                                          ...prev[investigationKey],
                                          ...val,
                                        },
                                      }))
                                    }
                                    template={template[investigationKey] || {}}
                                  />
                                </div>
                                <div className="form-group mt-2">
                                  <label
                                    htmlFor="commentDesc"
                                    className="form-label"
                                  >
                                    Template Description
                                  </label>
                                  <TextEditor
                                    editorValue={
                                      template[investigationKey]
                                        ?.template_description || ""
                                    }
                                    height={400}
                                    setEditorValue={(val) =>
                                      setTemplate((prev) => ({
                                        ...prev,
                                        [investigationKey]: {
                                          ...prev[investigationKey],
                                          template_description: val,
                                        },
                                      }))
                                    }
                                  />
                                </div>
                              </>
                            ) : (
                              <div key={obsIndex}>
                                {obsIndex === 0 && (
                                  <Table
                                    data={observations}
                                    tableColumns={add_observation_columns}
                                    showPagination={false}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {isInvestigationCulture(
                        department,
                        investigation_name
                      ) && (
                        <>
                          <div
                            style={{
                              width: "100%",
                              maxWidth: "100px",
                              marginTop: "10px",
                            }}
                          >
                            <select
                              style={{ width: "100%", padding: "5px" }}
                              onChange={(e) => {
                                handleInvestigationChange(e);
                                e.target.value = "";
                              }}
                            >
                              <option value="">- Select -</option>
                              {onlyOrganismData?.data?.data?.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.investigation_name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {selectedInvestigations.map((investigation) => (
                            <div
                              key={investigation.id}
                              style={{
                                marginBottom: "20px",
                                marginTop: "20px",
                              }}
                            >
                              <div
                                style={{
                                  display: "inline-flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "5px",
                                  border: "1px solid #ccc",
                                  borderRadius: "5px",
                                  marginBottom: "5px",
                                  backgroundColor: "#f5f5f5",
                                  maxWidth: "400px",
                                  minWidth: "100px",
                                }}
                              >
                                <strong>{investigation.name}</strong>
                                <button
                                  onClick={() =>
                                    handleRemoveInvestigation(investigation.id)
                                  }
                                  style={{
                                    background: "red",
                                    color: "white",
                                    border: "none",
                                    padding: "3px 6px",
                                    cursor: "pointer",
                                    borderRadius: "3px",
                                    marginLeft: "5px",
                                  }}
                                >
                                  ✕
                                </button>
                              </div>

                              {investigationDataMap[investigation.id] && (
                                <div
                                  style={{
                                    marginTop: "10px",
                                    padding: "5px",
                                    border: "1px solid #ddd",
                                    borderRadius: "5px",
                                  }}
                                >
                                  <Table
                                    data={
                                      investigationDataMap[investigation.id]
                                    }
                                    tableColumns={
                                      investigation_observation_columns
                                    }
                                    showPagination={false}
                                  />
                                </div>
                              )}
                            </div>
                          ))}
                        </>
                      )}
                      <div className="row mt-2">
                        <div className="col-md-4">
                          <h5 style={{ fontSize: "13px" }}>
                            Investigation Comments Name
                          </h5>
                          <CommentNameDropdown
                            investigationId={investigationIds}
                            value={commentNamesMap[investigationKey] || null}
                            setFilterValue={(selectedCommentName) =>
                              handleCommentNameChange(
                                department,
                                investigation_name,
                                selectedCommentName
                              )
                            }
                          />
                        </div>

                        <div className="col-md-8">
                          <h5 style={{ fontSize: "13px" }}>Comments</h5>
                          <TextEditor
                            editorValue={commentsMap[investigationKey] || ""}
                            height={200}
                            setEditorValue={(val) =>
                              setCommentsMap((prev) => ({
                                ...prev,
                                [investigationKey]: val,
                              }))
                            }
                          />

                          {/* <div
                            className="form-control"
                            contentEditable
                            onBlur={(e) =>
                              handleCommentChange(
                                department,
                                investigation_name,
                                e.currentTarget.innerHTML
                              )
                            }
                            dangerouslySetInnerHTML={{
                              __html: commentsMap[investigationKey] ?? "",
                            }}
                            style={{
                              minHeight: "40px",
                              resize: "both",
                              overflow: "auto",
                              padding: "8px",
                              fontSize: "13px",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                            }}
                          ></div> */}
                        </div>
                      </div>
                    </div>
                  );
                }
              )
          )}

        <div style={{ display: "flex", gap: "20px" }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {/* <div className="form-group mt-2">
              <h5 style={{ fontSize: "13px" }}>Investigation Comments Name</h5>
              <CommentNameDropdown
                investigationId={investigationId}
                setFilterValue={setCommentName}
              />
            </div> */}
            <div className="form-group">
              <h5 style={{ fontSize: "13px" }}>
                Internal Doctor{" "}
                {status === "APPROVED" && (
                  <span style={{ color: "red" }}> *</span>
                )}
              </h5>
              <InternalDoctorCheckbox
                employee_id={jwtAuthreducer?.user?.employee_code_id}
                setFilterValue={setFilterValue}
                doctorId={doctorId}
                isRequired={status === "APPROVED"}
                status={status}
              />
            </div>
            <div className="form-group mt-2" style={{ marginTop: "10px" }}>
              <h5 style={{ fontSize: "13px" }}>Status</h5>
              {reportRoleData?.data?.only_roles_data &&
                Object.keys(reportRoleData.data.only_roles_data).map(
                  (roleName) => (
                    <div
                      key={roleName}
                      style={{ display: "inline-block", marginRight: "10px" }}
                    >
                      <label>
                        <input
                          type="radio"
                          name="roleSelection"
                          value={roleName}
                          checked={status === roleName}
                          onChange={() => setStatus(roleName)}
                          style={{ marginRight: "5px" }}
                        />
                        {roleName}
                      </label>
                    </div>
                  )
                )}
            </div>
          </div>

          {/* <div className="form-group mt-2" style={{ flex: 1 }}>
            <h5 style={{ fontSize: "13px" }}>Comments</h5>
            <TextEditor editorValue={comment} height={155} setEditorValue={setComment} />
          </div> */}
        </div>
        <button
          type="submit"
          className="btn btn-primary mt-4"
          onClick={handleSave}
          // disabled={!status}
          // disabled={
          //   status === "APPROVED" && (!filterValue || filterValue === "")
          // }
          disabled={status === "APPROVED" && !filterValue && !doctorId}
        >
          Save
        </button>
      </div>
    </>
  );
};

export default AddObservationValue;
